import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { defineMessages, injectIntl } from 'react-intl';

import './index.sass';

import Box from '../../Molecules/Box';
import Text from '../../Atoms/Text';
import ProductImageGallery from '../../Molecules/ProductImageGallery';
import ProductQuantityControls from '../../Molecules/ProductQuantityControls';
import OtherProductModal from '../OtherProductModal';
import { Order, Product } from '../../../shared';
import { fetchProductGallery } from '../../../store/actions/products';
import { addProductToOrder, removeProductFromOrder } from '../../../store/actions/order';

class OtherProductsSection extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			selectedProduct: null,
			selectedImageIndex: 0,
			cardImageIndexes: {},
			galleryImagesByProductId: {},
		};
	}

	componentDidMount() {
		this.loadGalleryImages(this.props.otherProducts);
	}

	componentDidUpdate(prevProps) {
		const prevIds = (prevProps.otherProducts || []).map((product) => product.id).join(',');
		const nextIds = (this.props.otherProducts || []).map((product) => product.id).join(',');

		if (prevIds !== nextIds) {
			this.loadGalleryImages(this.props.otherProducts);
		}
	}

	loadGalleryImages = (otherProducts = []) => {
		(otherProducts || []).forEach((product) => {
			fetchProductGallery(product.id).then((images) => {
				this.setState((prevState) => ({
					galleryImagesByProductId: {
						...prevState.galleryImagesByProductId,
						[product.id]: images,
					},
				}));
			});
		});
	};

	getProductImages = (product) => Product.getImages(
		product,
		this.state.galleryImagesByProductId[product.id] || []
	);

	getProductQuantity = (productId) => {
		const { order: orderState } = this.props;
		const { order } = orderState;

		return Order.getProductQuantityOnOrder(order, productId);
	};

	isProductLoading = (productId) => {
		const { order: orderState } = this.props;
		const { productsBeingUpdated = [] } = orderState;

		return productsBeingUpdated.includes(productId);
	};

	getCardImageIndex = (productId) => this.state.cardImageIndexes[productId] || 0;

	handleOpenProduct = (product) => {
		this.setState({
			selectedProduct: product,
			selectedImageIndex: this.getCardImageIndex(product.id),
		});
	};

	handleCloseModal = () => {
		this.setState({ selectedProduct: null });
	};

	handleAddToCart = (productId, event) => {
		if (event) event.stopPropagation();
		if (!productId || this.isProductLoading(productId)) return;

		const { order: orderState, addProductToOrder: addProduct } = this.props;
		const { order } = orderState;
		const { id: orderId } = order;

		addProduct(orderId, productId);
	};

	handleRemoveFromCart = (productId, event) => {
		if (event) event.stopPropagation();
		if (!productId || this.isProductLoading(productId)) return;

		const {
			order: orderState,
			removeProductFromOrder: removeProduct,
		} = this.props;
		const { order } = orderState;
		const { id: orderId } = order;
		const orderItem = Order.getLastOrderItemByProductId(order, productId);

		if (!orderItem) return;

		removeProduct(orderId, orderItem.id, productId);
	};

	handleCardImageChange = (productId, imageIndex) => {
		this.setState((prevState) => ({
			cardImageIndexes: {
				...prevState.cardImageIndexes,
				[productId]: imageIndex,
			},
		}));
	};

	render() {
		const { otherProducts, intl } = this.props;
		const { selectedProduct, selectedImageIndex } = this.state;
		const { formatMessage } = intl;
		const messages = defineMessages({
			title: {
				id: 'gallery.otherProducts.title',
				defaultMessage: 'Produtos disponíveis',
			},
			help: {
				id: 'gallery.otherProducts.help',
				defaultMessage: 'Clique em um produto para ver detalhes ou adicione quantos quiser ao carrinho.',
			},
			inCartCount: {
				id: 'gallery.otherProducts.inCartCount',
				defaultMessage: '{quantity}x',
			},
		});

		if (!otherProducts || otherProducts.length === 0) {
			return null;
		}

		const selectedImages = selectedProduct
			? this.getProductImages(selectedProduct)
			: [];

		return (
			<div className="OtherProductsSection" id="other-products">
				<Text className="OtherProductsSection__title Gallery__help" align="center" variant="h6">
					{formatMessage(messages.title)}
				</Text>
				<Text className="OtherProductsSection__help Gallery__help Gallery--noPadding" align="center">
					{formatMessage(messages.help)}
				</Text>
				<Box className="PhotoGallery__wrapper" borderPosition="none">
					<ul className="OtherProductsSection__list">
						{otherProducts.map((product) => {
							const images = this.getProductImages(product);
							const quantity = this.getProductQuantity(product.id);
							const isLoading = this.isProductLoading(product.id);

							return (
								<li
									className={`OtherProductsSection__item ${quantity > 0 ? 'OtherProductsSection__item--inCart' : ''}`}
									key={product.id}
									onClick={() => this.handleOpenProduct(product)}
								>
									{quantity > 0 && (
										<span className="OtherProductsSection__badge">
											{formatMessage(messages.inCartCount, { quantity })}
										</span>
									)}
									<div className="OtherProductsSection__gallery">
										<ProductImageGallery
											images={images}
											activeIndex={this.getCardImageIndex(product.id)}
											onChangeIndex={(index) => this.handleCardImageChange(product.id, index)}
											imageType="thumb"
										/>
									</div>
									<Text className="OtherProductsSection__name" variant="body2">
										{product.name}
									</Text>
									<div
										className="OtherProductsSection__action"
										onClick={(event) => event.stopPropagation()}
									>
										<ProductQuantityControls
											quantity={quantity}
											isLoading={isLoading}
											onAdd={(event) => this.handleAddToCart(product.id, event)}
											onRemove={(event) => this.handleRemoveFromCart(product.id, event)}
										/>
									</div>
								</li>
							);
						})}
					</ul>
				</Box>
				<OtherProductModal
					isOpened={selectedProduct !== null}
					onClose={this.handleCloseModal}
					product={selectedProduct}
					images={selectedImages}
					activeImageIndex={selectedImageIndex}
					onChangeImageIndex={(index) => this.setState({ selectedImageIndex: index })}
					quantity={selectedProduct ? this.getProductQuantity(selectedProduct.id) : 0}
					isLoading={selectedProduct ? this.isProductLoading(selectedProduct.id) : false}
					onAdd={(event) => this.handleAddToCart(selectedProduct && selectedProduct.id, event)}
					onRemove={(event) => this.handleRemoveFromCart(selectedProduct && selectedProduct.id, event)}
				/>
			</div>
		);
	}
}

OtherProductsSection.propTypes = {
	otherProducts: PropTypes.arrayOf(PropTypes.object),
	order: PropTypes.shape({
		order: PropTypes.object,
		productsBeingUpdated: PropTypes.arrayOf(PropTypes.string),
	}),
	addProductToOrder: PropTypes.func.isRequired,
	removeProductFromOrder: PropTypes.func.isRequired,
	intl: PropTypes.object.isRequired,
};

OtherProductsSection.defaultProps = {
	otherProducts: [],
	order: {
		order: {},
		productsBeingUpdated: [],
	},
};

const mapStateToProps = (state) => ({
	order: state.order,
});

const mapActionsToProps = (dispatch) => ({
	addProductToOrder: (orderId, productId) => dispatch(addProductToOrder(orderId, productId)),
	removeProductFromOrder: (orderId, orderItemId, productId) => (
		dispatch(removeProductFromOrder(orderId, orderItemId, productId))
	),
});

export default connect(mapStateToProps, mapActionsToProps)(injectIntl(OtherProductsSection));
